import { useCallback, useEffect, useState } from 'react'
import { X } from 'lucide-react'

import { Checkbox } from '@/components/ui'
import { IColumn, Table } from '@/core/components/Table'
import { getVendorTags, IVendorTag } from '@/core/services/vendors'
import { CreateTagModal } from '@/home/components/vendors/CreateTagModal'
import { formatDateTime } from '@/core/constants/times'
import Loader from '@/common/components/Loader'

interface TagsTabProps {
  vendorId: string
  onError: (message: string) => void
  createModalOpen: boolean
  onCreateModalChange: (open: boolean) => void
}

export const TagsTab = ({
  vendorId,
  onError,
  createModalOpen,
  onCreateModalChange,
}: TagsTabProps) => {
  const [tags, setTags] = useState<IVendorTag[]>([])
  const [isCheckAll, setIsCheckAll] = useState(false)
  const [isCheck, setIsCheck] = useState<string[]>([])
  const [editTag, setEditTag] = useState<IVendorTag | null>(null)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const fetchTags = useCallback(async () => {
    if (!vendorId) return
    setLoading(true)
    try {
      const { data } = await getVendorTags(vendorId)
      setTags(data)
    } catch (e) {
      if (e instanceof Error) onError(e.message)
    } finally {
      setLoading(false)
    }
  }, [vendorId])

  useEffect(() => {
    fetchTags()
  }, [fetchTags])

  const handleSelectAll = () => {
    setIsCheckAll(!isCheckAll)
    setIsCheck(!isCheckAll ? tags.map((t) => t.id) : [])
  }

  const handleClick = (id: string, checked: boolean) => {
    setIsCheck(checked ? [...isCheck, id] : isCheck.filter((i) => i !== id))
  }

  const handleNameClick = (tag: IVendorTag) => {
    setEditTag(tag)
    setEditModalOpen(true)
  }

  const handleEditModalChange = (open: boolean) => {
    setEditModalOpen(open)
    if (!open) setEditTag(null)
  }

  const handleSuccess = () => {
    setIsCheck([])
    setIsCheckAll(false)
    fetchTags()
  }

  const columns: IColumn[] = [
    {
      title: (
        <Checkbox onCheckedChange={handleSelectAll} checked={isCheckAll} />
      ),
      accessor: '',
      render: (row: IVendorTag) => (
        <Checkbox
          onCheckedChange={(checked: boolean) => handleClick(row.id, checked)}
          checked={isCheck.includes(row.id)}
        />
      ),
    },
    {
      title: 'Name',
      accessor: 'name',
      sortable: true,
      searchable: true,
      render: (row: IVendorTag) => (
        <a
          className="text-accent-1 underline cursor-pointer"
          href="#"
          onClick={(e) => {
            e.preventDefault()
            handleNameClick(row)
          }}
        >
          {row.name}
        </a>
      ),
    },
    {
      title: 'Created Date',
      accessor: 'createdAt',
      sortable: true,
      render: (row: IVendorTag) =>
        row.createdAt ? formatDateTime(row.createdAt) : '—',
    },
    {
      title: 'Edit',
      accessor: '',
      render: (row: IVendorTag) => (
        <a
          className="text-accent-1 underline cursor-pointer"
          href="#"
          onClick={(e) => {
            e.preventDefault()
            handleNameClick(row)
          }}
        >
          Edit
        </a>
      ),
    },
    {
      title: '',
      accessor: '',
      render: (row: IVendorTag) =>
        isCheck.includes(row.id) ? (
          <button
            type="button"
            className="flex h-6 w-6 items-center justify-center rounded-full bg-monochrome-5/70 text-monochrome-1 hover:bg-red-500/30 hover:text-red-400 transition-colors cursor-pointer"
            onClick={() => handleNameClick(row)}
          >
            <X className="h-4 w-4" />
          </button>
        ) : null,
    },
  ]

  return (
    <>
      {loading && <Loader />}
      {tags.length ? (
        <Table
          className="[&_th]:!whitespace-nowrap"
          columns={columns}
          data={tags}
          isCheck={isCheck}
        />
      ) : (
        <div className="flex items-center justify-center py-20 text-monochrome-2">
          No Tags data available
        </div>
      )}

      <CreateTagModal
        open={createModalOpen}
        onOpenChange={onCreateModalChange}
        vendorId={vendorId}
        onSuccess={handleSuccess}
        onError={onError}
      />

      <CreateTagModal
        open={editModalOpen}
        onOpenChange={handleEditModalChange}
        vendorId={vendorId}
        tag={editTag}
        onSuccess={handleSuccess}
        onError={onError}
      />
    </>
  )
}
